import { Trans, useTranslation } from 'next-i18next';
import { memo, useEffect } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { ValidationError } from '@bridebook/toolbox/src';
import {
  Box,
  ButtonV2,
  CustomNameValueEvent,
  Datepicker,
  DropdownV2,
  ImageX,
  InputV2,
  Rating,
  SupportMessage,
  TextArea,
} from '@bridebook/ui';
import { OnChangeHandler } from '@bridebook/ui/src/components/bbcommon/inputs/dropdown/dropdown';
import PhotoUploader from '@bridebook/web/components/photo-uploader';
import type { IS3File } from '@bridebook/ui/src/types';
import {
  getLocalisedMonths,
  getLocalisedWeekdays,
} from '@bridebook/web/lib/datepicker/utils/localised-calendar-props';
import {
  ReviewFromType,
  getReviewFromOptions,
  getYesterday,
} from '@bridebook/web/lib/supplier/utils';
import { ReviewForm } from '@bridebook/web/lib/types';
import { styles } from './review-supplier-form.style';
import { Text } from '@bridebook/aura';
import { FullMobileModalV2 } from '@bridebook/web/components/common/full-mobile-modal-v2/full-mobile-modal-v2';
import { useMarket } from '../../lib/i18n/hooks/use-market';

const EMAIL_PATTERN =
  '[\\p{L}\\p{N}\\p{P}\\p{Sm}]+@([\\p{L}\\p{N}]([\\p{Pd}\\p{L}\\p{N}]{0,61}[\\p{L}\\p{N}])?[.])+\\p{L}{2,10}';

export interface IReviewSupplierFormProps {
  supplier: ISupplier;
  reviewsPage: boolean;
  reviewForm: ReviewForm;
  reviewFormShow: boolean;
  isMobile: boolean;
  onChange(e: CustomNameValueEvent): void;
  onUploadFinish(s3file: IS3File): void;
  onSaveReview(): void;
  onSetReviewFormRating(stars: number): void;
  onHideReviewFormError(): void;
}

const ReviewSupplierFormComponent = ({
  reviewsPage,
  supplier,
  reviewForm,
  reviewFormShow,
  isMobile,
  onChange,
  onSaveReview,
  onSetReviewFormRating,
  onHideReviewFormError,
  onUploadFinish,
}: IReviewSupplierFormProps) => {
  const { t } = useTranslation('supplier');
  const { error, showError, fields, uploadPhotoSuccess } = reviewForm;

  useEffect(() => {
    if (error && reviewFormShow) {
      window.scrollTo(0, 0);
    }
  }, [error, reviewFormShow]);

  const onSelectChanged: OnChangeHandler<ReviewFromType> = ({ name, value }) =>
    onChange({ target: { value, name } });

  const onMessageChange = (value: string) => onChange({ target: { value, name: 'message' } });

  const saveDate = (value: string) => onChange({ target: { value, name: 'weddingDate' } });

  const slug = supplier.type?.[0];
  const publicId = supplier.publicId;
  const name = supplier.name;
  const emailLabelInfo = t('form.review.wont.show.label');
  const directory = `reviews/${slug}/${publicId}`;
  const errorMessage =
    error instanceof ValidationError && error.prop ? t('form.review.error.missingFields') : '';
  const reviewByOptions = getReviewFromOptions();
  const reviewBySelected = reviewByOptions.find((option) => option.value === fields.from);
  const { locale } = useMarket();

  return (
    <>
      <FullMobileModalV2.Header>
        {!isMobile || reviewsPage ? (
          <Box style={styles.header}>
            <Text variant="typo-xl-semibold">{`${t('form.review.heading.writeReviewFor')} ${name}`}</Text>
          </Box>
        ) : (
          t('form.review.heading.writeReview')
        )}
      </FullMobileModalV2.Header>
      <FullMobileModalV2.Content>
        <Box style={styles.wrapper}>
          {showError && errorMessage && (
            <SupportMessage onClick={onHideReviewFormError} theme="error">
              {errorMessage}
            </SupportMessage>
          )}
          <Box style={styles.formField}>
            <Text variant="typo-base-semibold" id="supplier-rating">
              {t('form.review.rate.input.label')}*
            </Text>
            <Rating
              rating={fields.stars}
              onChange={onSetReviewFormRating}
              readonly={false}
              width={20}
              overrideColor="peachRose120"
              starGap={4}
            />
            {error && error.prop === 'stars' && (
              <Box style={styles.errorMsg}>{error.messageI18n || error.message}</Box>
            )}
          </Box>

          <Box style={styles.formField}>
            <Text variant="typo-base-semibold">{t('form.review.title.label')}*</Text>
            <InputV2
              id="input-supplier-review-title"
              name="title"
              onChange={onChange}
              required
              value={fields.title}
              placeholder={t('form.review.title.input.placeholder')}
              error={!!error}
            />
          </Box>
          <Box style={styles.formField}>
            <Text variant="typo-base-semibold">{t('form.review.message.label')}*</Text>
            <TextArea
              name="message"
              rows={isMobile ? 5 : 13}
              id="input-supplier-review-message"
              value={fields.message}
              onChange={onMessageChange}
              placeholder={t('form.review.message.input.placeholder')}
              error={!!error}
            />
          </Box>
          <Box style={styles.formField}>
            <Text variant="typo-base-semibold">{t('form.review.photo.input.label')}</Text>
            <Box style={styles.uploadWrapper}>
              {fields.photo && fields.photo.public_id && (
                <>
                  {uploadPhotoSuccess && (
                    <Text variant="typo-sm">{t('form.review.msg.upload.success')}</Text>
                  )}
                  <Box style={styles.photoWrapper}>
                    <ImageX src={fields.photo.imgixURL} w={270} h={150} />
                  </Box>
                </>
              )}
              <PhotoUploader
                isMobile={isMobile}
                directory={directory}
                multiple={false}
                onUploadFinish={onUploadFinish}
              />
            </Box>
          </Box>
          <Box style={styles.formField}>
            <Text variant="typo-base-semibold">{t('form.review.details.title')}*</Text>
            <Box style={styles.detailsForm}>
              <DropdownV2
                name="from"
                label={t('form.review.from.input.label')}
                onSelect={onSelectChanged}
                options={getReviewFromOptions()}
                value={reviewBySelected?.label}
                error={!!(error && error.prop === 'from' ? error : null)}
              />
              <Datepicker
                id="wedding-date"
                label={t('form.review.date.input.label')}
                value={fields.weddingDate}
                placeholder={t('form.review.date.input.placeholder')}
                onChangeFormatted={saveDate}
                returnFormat="yyyy-MM-dd"
                disableAfter={getYesterday()}
                months={getLocalisedMonths(locale)}
                weekdaysShort={getLocalisedWeekdays('short', 0)}
              />
              <InputV2
                label={t('form.review.name.input.label')}
                id="input-supplier-review-name"
                value={fields.name}
                onChange={onChange}
                name="name"
                placeholder={t('form.review.name.input.placeholder')}
                error={!!error}
              />
              <InputV2
                id="input-supplier-review-mail"
                label={t('form.review.email.input.label')}
                type="email"
                value={fields.email}
                onChange={onChange}
                name="email"
                placeholder={t('form.review.email.input.placeholder')}
                pattern={EMAIL_PATTERN}
                error={!!error}
                helper={emailLabelInfo}
              />
            </Box>
          </Box>

          <Box style={styles.infoText}>
            <Trans ns="supplier" i18nKey="termsDisclaimer">
              All reviews submitted will be verified and posted within 24 hours.
              <Box as="br" />
              By submitting this review you are verifying that it is an honest and accurate review.
              <Box as="br" />
              Bridebook reserves the right to request evidence of any feedback provided.
            </Trans>
          </Box>
          <Box style={styles.submitWrapper}>
            <ButtonV2 onClick={onSaveReview}>
              {reviewsPage
                ? t('form.review.submit.button.subminAndAddAnother')
                : t('form.review.submit.button.addReview')}
            </ButtonV2>
          </Box>
        </Box>
      </FullMobileModalV2.Content>
    </>
  );
};

export const ReviewSupplierForm = memo(ReviewSupplierFormComponent);
